// /src/components/public/publicSiteRegistry.mjs
// Shared public-site release and route registry.
// This file is intentionally dependency-free so React, Express, sitemap,
// navigation, and deployment verification use the same canonical definitions.

export const PUBLIC_RELEASE_ID = 'taskersys-public-sync-2026.11.03-r5';
export const PUBLIC_BASE_URL = 'https://taskersys.com';

const action = (to, label) => Object.freeze({ to, label });

const PAGE_DEFINITIONS = {
  '/': {
    path: '/',
    kind: 'home',
    schemaType: 'WebPage',
    indexable: true,
    title: 'NYC Property Services & Operations Platform | TaskerSys',
    description:
      'TaskerSys combines NYC field services for apartment turnovers, maintenance, cleaning and renovation with a property-operations platform for scopes, approvals, photos, budgets and closeout.',
    eyebrow: 'PROPERTY OPERATIONS, CONNECTED',
    heading: 'Property operations software and NYC field services.',
    lead:
      'Use TaskerSys to coordinate property work, engage our field teams for a defined scope, or combine both. Keep maintenance, turnovers, approvals, photographs, costs, and closeout moving together.',
    primaryAction: action('/services', 'Explore field services'),
    secondaryAction: action('/platform', 'Explore the platform'),
    heroNote:
      'For property management companies, building owners, owner-operators, and portfolio teams across New York City, including the five boroughs.',
  },
  '/services': {
    path: '/services',
    kind: 'services',
    schemaType: 'CollectionPage',
    navLabel: 'Field Services',
    indexable: true,
    title: 'NYC Property Field Services | TaskerSys',
    description:
      'Explore TaskerSys field services for NYC apartment turnovers, property maintenance, cleaning, renovation, common-area work and hotel renovation, with defined scopes and documented closeout.',
    eyebrow: 'TASKERSYS FIELD SERVICES',
    heading: 'NYC field capacity for property work that needs a clear owner.',
    lead:
      'Use TaskerSys for apartment turnovers, maintenance backlogs, cleaning, common-area programs, renovation, and hotel project phases—without replacing the operating relationships that already work.',
    primaryAction: action('/contact', 'Discuss a pilot scope'),
    secondaryAction: action('/projects', 'View project gallery'),
    heroNote:
      'Final scope, scheduling, and pricing depend on the property, condition, access, quantity, timing, and approval structure.',
  },
  '/services/apartment-turnovers-nyc': {
    path: '/services/apartment-turnovers-nyc',
    kind: 'service',
    schemaType: 'WebPage',
    serviceSlug: 'apartment-turnovers-nyc',
    indexable: true,
    title: 'NYC Apartment Turnovers and Make-Ready Support | TaskerSys',
    description:
      'Apartment turnover support for NYC property managers, including punchlists, patching, painting, small carpentry, flooring repairs, cleaning, final readiness checks, and photo-based closeout.',
    eyebrow: 'APARTMENT TURNOVERS',
    heading: 'NYC apartment turnovers and make-ready support',
    lead:
      'Add organized field capacity when vacant units, simultaneous turnovers, or accumulated punchlists exceed the building team’s available time.',
    primaryAction: action('/contact', 'Request a scope review'),
    secondaryAction: action('/projects', 'View project gallery'),
  },
  '/services/property-maintenance-nyc': {
    path: '/services/property-maintenance-nyc',
    kind: 'service',
    schemaType: 'WebPage',
    serviceSlug: 'property-maintenance-nyc',
    indexable: true,
    title: 'NYC Property Maintenance and Repair Capacity | TaskerSys',
    description:
      'Property maintenance and repair support for NYC managers, including defined punchlists, repair backlogs, common-area work, multi-trade assignments, and additional crews for concentrated scopes.',
    eyebrow: 'PROPERTY MAINTENANCE',
    heading: 'NYC property maintenance, repair backlogs, and project capacity',
    lead:
      'Use TaskerSys when routine building staff and existing vendors cover daily operations but additional work still needs a dependable owner, schedule, and closeout path.',
    primaryAction: action('/contact', 'Request a scope review'),
    secondaryAction: action('/projects', 'View project gallery'),
  },
  '/services/property-cleaning-nyc': {
    path: '/services/property-cleaning-nyc',
    kind: 'service',
    schemaType: 'WebPage',
    serviceSlug: 'property-cleaning-nyc',
    indexable: true,
    title: 'NYC Turnover and Property Cleaning | TaskerSys',
    description:
      'Cleaning services for NYC property operations, including turnover cleaning, post-renovation cleaning, shared-apartment cleaning, common-area cleaning, final detail work, and photo-based completion reporting.',
    eyebrow: 'PROPERTY CLEANING',
    heading: 'NYC turnover, post-renovation, shared-apartment, and common-area cleaning',
    lead:
      'Keep vacant units, occupied shared apartments, and building common areas presentable and ready on a dependable schedule.',
    primaryAction: action('/contact', 'Request a scope review'),
    secondaryAction: action('/projects', 'View project gallery'),
  },
  '/services/renovation-nyc': {
    path: '/services/renovation-nyc',
    kind: 'service',
    schemaType: 'WebPage',
    serviceSlug: 'renovation-nyc',
    indexable: true,
    title: 'NYC Apartment and Common-Area Renovation | TaskerSys',
    description:
      'Defined renovation and refresh projects for NYC apartments and common areas, including painting, flooring, drywall, finish carpentry, doors, hardware, fixtures, and coordinated multi-trade execution.',
    eyebrow: 'RENOVATION & REFRESH',
    heading: 'NYC apartment, common-area, and property refresh projects',
    lead:
      'Use a structured project approach for scopes that are larger than routine maintenance but do not require the complexity of a major ground-up construction engagement.',
    primaryAction: action('/contact', 'Request a scope review'),
    secondaryAction: action('/projects', 'View project gallery'),
  },
  '/services/hotel-renovation': {
    path: '/services/hotel-renovation',
    kind: 'service',
    schemaType: 'WebPage',
    serviceSlug: 'hotel-renovation',
    indexable: true,
    title: 'Hotel Guest-Room and Common-Space Renovation | TaskerSys',
    description:
      'Hotel guest-room and common-area renovation support with phased scheduling, multi-trade coordination, progress reporting, punchlist management, and closeout planning for operating properties.',
    eyebrow: 'HOTEL RENOVATION',
    heading: 'Hotel guest-room and common-space renovation support',
    lead:
      'Plan guest-room, corridor, lobby, and shared-space improvements around occupancy, operational constraints, material readiness, brand standards, and controlled handoffs.',
    primaryAction: action('/contact', 'Request a scope review'),
    secondaryAction: action('/projects', 'View project gallery'),
  },
  '/platform': {
    path: '/platform',
    kind: 'platform',
    schemaType: 'WebPage',
    navLabel: 'Platform',
    indexable: true,
    title: 'Property Operations Platform | TaskerSys',
    description:
      'TaskerSys connects service requests, buildings, managers, service providers, dispatchers, technicians, photos, approvals, budgets, invoices, payroll and property-work closeout.',
    eyebrow: 'TASKERSYS PLATFORM',
    heading: 'A connected operating system for property work.',
    lead:
      'Coordinate requests, scopes, buildings, people, schedules, photos, approvals, budgets, invoices, payroll, and closeout—without losing the connection between management and field execution.',
    primaryAction: action('/contact', 'Request a walkthrough'),
    secondaryAction: action('/signup', 'Create an account'),
    heroNote:
      'Use the platform independently, connect it to your existing providers, or combine it with TaskerSys field services.',
  },
  '/projects': {
    path: '/projects',
    kind: 'projects',
    schemaType: 'CollectionPage',
    navLabel: 'Projects',
    indexable: true,
    title: 'TaskerSys Project Gallery | Property Field Work',
    description:
      'Browse TaskerSys project and field-work galleries for apartment turnovers, maintenance, cleaning, furnishing, renovation, common-area work, and other property-service categories.',
    eyebrow: 'PROJECT GALLERY',
    heading: 'See field activity and completed work in context.',
    lead:
      'Browse the project media currently published for TaskerSys field services. Open a category and gallery to review the available before, progress, and after views.',
    primaryAction: action('/contact', 'Discuss a similar scope'),
    secondaryAction: action('/services', 'Review field services'),
  },
  '/about': {
    path: '/about',
    kind: 'about',
    schemaType: 'AboutPage',
    navLabel: 'About',
    indexable: true,
    title: 'About TaskerSys | Property Operations Platform & NYC Field Services',
    description:
      'Learn how TaskerSys, operated by Copala Inc., connects property-management decisions, field execution, documentation and financial closeout through software and optional NYC field services.',
    eyebrow: 'ABOUT TASKERSYS',
    heading: 'Built to connect property decisions with field execution.',
    lead:
      'TaskerSys is a property-operations platform with optional NYC field services, operated by Copala Inc. Use the software, add field capacity for a defined assignment, or connect both without replacing the relationships that already work.',
    primaryAction: action('/platform', 'Explore the platform'),
    secondaryAction: action('/services', 'Explore field services'),
  },
  '/contact': {
    path: '/contact',
    kind: 'contact',
    schemaType: 'ContactPage',
    navLabel: 'Contact',
    indexable: true,
    title: 'Contact TaskerSys | Property Services & Platform',
    description:
      'Contact TaskerSys about NYC property field services, apartment turnovers, maintenance, cleaning, renovation, hotel projects, pilot assignments, or the TaskerSys property-operations platform.',
    eyebrow: 'CONTACT TASKERSYS',
    heading: 'Tell us about the property, the work, or the workflow.',
    lead:
      'A useful first message explains the location, scope, quantity, current condition, target timing, access requirements, and who can approve the next step.',
    heroNote:
      'Contact us about field services, a pilot assignment, platform access, or a specific scope that needs pricing.',
  },
  '/privacy-policy': {
    path: '/privacy-policy',
    kind: 'legal',
    schemaType: 'WebPage',
    indexable: false,
    title: 'Privacy Policy | TaskerSys',
    description:
      'Learn how TaskerSys and Copala Inc. collect, use, share, retain and protect information, including optional Google Drive receipt-storage data.',
    eyebrow: 'LEGAL',
    heading: 'Privacy Policy',
    lead:
      'Review the privacy information that applies to the TaskerSys website, inquiries, accounts, uploaded information, and application use.',
  },
  '/terms-of-service': {
    path: '/terms-of-service',
    kind: 'legal',
    schemaType: 'WebPage',
    indexable: false,
    title: 'Terms of Service | TaskerSys',
    description:
      'Terms governing use of the TaskerSys property-operations platform, public website, accounts, optional field services, Google Drive integration and related services.',
    eyebrow: 'LEGAL',
    heading: 'Terms of Service',
    lead:
      'Review the terms that apply to use of the TaskerSys website, public services, accounts, and application.',
  },
};

export const PUBLIC_PAGE_DEFINITIONS = Object.freeze(
  Object.fromEntries(
    Object.entries(PAGE_DEFINITIONS).map(([path, page]) => [path, Object.freeze({ ...page })])
  )
);

export const PUBLIC_NAVIGATION = Object.freeze(
  ['/services', '/platform', '/projects', '/about', '/contact'].map((path) => {
    const page = PUBLIC_PAGE_DEFINITIONS[path];
    return Object.freeze({ path, label: page.navLabel });
  })
);

export const INDEXABLE_PUBLIC_ROUTES = Object.freeze(
  Object.values(PUBLIC_PAGE_DEFINITIONS)
    .filter((page) => page.indexable)
    .map((page) => page.path)
);

export const LEGAL_PUBLIC_ROUTES = Object.freeze([
  '/privacy-policy',
  '/terms-of-service',
]);

export const PUBLIC_SERVER_RENDERED_ROUTES = Object.freeze([
  ...INDEXABLE_PUBLIC_ROUTES,
  ...LEGAL_PUBLIC_ROUTES,
]);

export const PUBLIC_UTILITY_ROUTES = Object.freeze([
  '/signup',
  '/signin',
  '/forgot-password',
  '/reset-password',
  '/oauth-success',
]);

export function normalizePublicRegistryPath(pathname = '/') {
  const raw = String(pathname || '/').split('?')[0].split('#')[0];
  if (!raw || raw === '/') return '/';
  return raw.replace(/\/+$/, '').toLowerCase() || '/';
}

export function getPublicPageDefinition(pathname = '/') {
  return PUBLIC_PAGE_DEFINITIONS[normalizePublicRegistryPath(pathname)] || null;
}

